import React, { useState, useEffect } from 'react';
import { BACKEND_URL } from '../../config';
import { motion } from 'framer-motion';
import {
  ArrowsClockwise,
  BookOpen,
  ChartBar,
  CheckCircle,
  DownloadSimple,
  Envelope,
  FileXls,
  Funnel,
  TrendUp,
  UsersThree,
  XCircle
} from '@phosphor-icons/react';

const EbookDownloadsManagement = () => {
  const [downloads, setDownloads] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [filter, setFilter] = useState('all'); // 'all', 'opted_in', 'opted_out'
  const [selectedEbook, setSelectedEbook] = useState('all');
  const [ebooks, setEbooks] = useState([]);
  const [pagination, setPagination] = useState({ skip: 0, limit: 50, total: 0, hasMore: false });

  const fetchDownloads = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('admin_token');
      let url = `${BACKEND_URL}/api/admin/ebook-downloads?skip=${pagination.skip}&limit=${pagination.limit}`;
      
      if (filter === 'opted_in') {
        url += '&marketing_consent=true';
      } else if (filter === 'opted_out') {
        url += '&marketing_consent=false';
      }
      
      if (selectedEbook !== 'all') {
        url += `&ebook_id=${selectedEbook}`;
      }
      
      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setDownloads(data.downloads);
        setPagination(prev => ({
          ...prev,
          total: data.total,
          hasMore: data.has_more
        }));
      }
    } catch (error) {
      console.error('Error fetching downloads:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchSummary = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${BACKEND_URL}/api/admin/ebook-downloads/summary`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setSummary(data);
      }
    } catch (error) {
      console.error('Error fetching summary:', error);
    }
  };

  const fetchEbooks = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${BACKEND_URL}/api/admin/ebooks`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setEbooks(data);
      }
    } catch (error) {
      console.error('Error fetching ebooks:', error);
    }
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const token = localStorage.getItem('admin_token');
      let url = `${BACKEND_URL}/api/admin/ebook-downloads/export`;
      
      const params = new URLSearchParams();
      if (filter === 'opted_in') {
        params.append('marketing_consent', 'true');
      } else if (filter === 'opted_out') {
        params.append('marketing_consent', 'false');
      }
      
      if (selectedEbook !== 'all') {
        params.append('ebook_id', selectedEbook);
      }
      
      if (params.toString()) {
        url += `?${params.toString()}`;
      }
      
      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const blob = await response.blob();
        const downloadUrl = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = downloadUrl;
        
        // Get filename from header or use default
        const contentDisposition = response.headers.get('Content-Disposition');
        const filename = contentDisposition 
          ? contentDisposition.split('filename=')[1]?.replace(/"/g, '')
          : `ebook_downloads_${new Date().toISOString().split('T')[0]}.xlsx`;
        
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(downloadUrl);
      }
    } catch (error) {
      console.error('Error exporting:', error);
    } finally {
      setExporting(false);
    }
  };

  useEffect(() => {
    fetchEbooks();
    fetchSummary();
  }, []);

  useEffect(() => {
    fetchDownloads();
  }, [filter, selectedEbook, pagination.skip]);

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString();
  };

  return (
    <div className="p-6 bg-[#161B26] min-h-screen">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl font-bold text-white flex items-center gap-3">
              <Envelope className="w-7 h-7 text-[#6366F1]" />
              Ebook DownloadSimple Leads
            </h1>
            <p className="text-[#718096] mt-1">Track and manage ebook download leads</p>
          </div>
          
          <div className="flex items-center gap-3">
            <button
              onClick={() => { fetchDownloads(); fetchSummary(); }}
              className="flex items-center gap-2 px-4 py-2 bg-[#1A1D24] text-white rounded-lg hover:bg-[#2D3748] transition-colors"
            >
              <ArrowsClockwise className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              ArrowsClockwise
            </button>
            
            <button
              onClick={handleExport}
              disabled={exporting}
              className="flex items-center gap-2 px-4 py-2 bg-[#6366F1] text-white rounded-lg hover:bg-[#5558E3] transition-colors disabled:opacity-50"
            >
              <FileXls className={`w-4 h-4 ${exporting ? 'animate-pulse' : ''}`} />
              {exporting ? 'Exporting...' : 'Export to Excel'}
            </button>
          </div>
        </div>

        {/* Summary Cards */}
        {summary && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-[#1A1D24] rounded-xl p-5 border border-[#2D3748]"
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-[#718096] text-sm">Total Downloads</p>
                  <p className="text-3xl font-bold text-white mt-1">{summary.total_downloads}</p>
                </div>
                <div className="w-12 h-12 rounded-lg bg-[#6366F1]/20 flex items-center justify-center">
                  <DownloadSimple className="w-6 h-6 text-[#6366F1]" />
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="bg-[#1A1D24] rounded-xl p-5 border border-[#2D3748]"
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-[#718096] text-sm">Unique Emails</p>
                  <p className="text-3xl font-bold text-white mt-1">{summary.unique_emails}</p>
                </div>
                <div className="w-12 h-12 rounded-lg bg-[#10B981]/20 flex items-center justify-center">
                  <UsersThree className="w-6 h-6 text-[#10B981]" />
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="bg-[#1A1D24] rounded-xl p-5 border border-[#2D3748]"
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-[#718096] text-sm">Marketing Opt-in</p>
                  <p className="text-3xl font-bold text-white mt-1">{summary.marketing_opted_in}</p>
                </div>
                <div className="w-12 h-12 rounded-lg bg-[#EC4899]/20 flex items-center justify-center">
                  <CheckCircle className="w-6 h-6 text-[#EC4899]" />
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="bg-[#1A1D24] rounded-xl p-5 border border-[#2D3748]"
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-[#718096] text-sm">Opt-in Rate</p>
                  <p className="text-3xl font-bold text-white mt-1">{summary.opt_in_rate}%</p>
                </div>
                <div className="w-12 h-12 rounded-lg bg-[#F59E0B]/20 flex items-center justify-center">
                  <TrendUp className="w-6 h-6 text-[#F59E0B]" />
                </div>
              </div>
            </motion.div>
          </div>
        )}

        {/* Downloads by Ebook */}
        {summary && summary.downloads_by_ebook && summary.downloads_by_ebook.length > 0 && (
          <div className="bg-[#1A1D24] rounded-xl p-5 border border-[#2D3748] mb-8">
            <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
              <ChartBar className="w-5 h-5 text-[#6366F1]" />
              Downloads by Ebook
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {summary.downloads_by_ebook.map((item, index) => (
                <div key={item.ebook_id || index} className="bg-[#161B26] rounded-lg p-4">
                  <div className="flex items-center gap-3">
                    <BookOpen className="w-5 h-5 text-[#6366F1]" />
                    <div className="flex-1 min-w-0">
                      <p className="text-white font-medium truncate">{item.ebook_title}</p>
                      <p className="text-[#718096] text-sm">{item.count} downloads</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="bg-[#1A1D24] rounded-xl p-5 border border-[#2D3748] mb-6">
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex items-center gap-2">
              <Funnel className="w-4 h-4 text-[#718096]" />
              <span className="text-[#718096] text-sm">Funnel:</span>
            </div>
            
            <div className="flex items-center gap-2">
              <label className="text-[#A0AEC0] text-sm">Marketing Consent:</label>
              <select
                value={filter}
                onChange={(e) => { setFilter(e.target.value); setPagination(prev => ({ ...prev, skip: 0 })); }}
                className="bg-[#161B26] border border-[#2D3748] rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-[#6366F1]"
              >
                <option value="all">All</option>
                <option value="opted_in">Opted In</option>
                <option value="opted_out">Opted Out</option>
              </select>
            </div>
            
            <div className="flex items-center gap-2">
              <label className="text-[#A0AEC0] text-sm">Ebook:</label>
              <select
                value={selectedEbook}
                onChange={(e) => { setSelectedEbook(e.target.value); setPagination(prev => ({ ...prev, skip: 0 })); }}
                className="bg-[#161B26] border border-[#2D3748] rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-[#6366F1] max-w-xs"
              >
                <option value="all">All Ebooks</option>
                {ebooks.map(ebook => (
                  <option key={ebook.id} value={ebook.id}>{ebook.title}</option>
                ))}
              </select>
            </div>
            
            <div className="ml-auto text-[#718096] text-sm">
              Showing {downloads.length} of {pagination.total} records
            </div>
          </div>
        </div>

        {/* Downloads Table */}
        <div className="bg-[#1A1D24] rounded-xl border border-[#2D3748] overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-[#161B26] border-b border-[#2D3748]">
                  <th className="text-left px-6 py-4 text-[#A0AEC0] font-medium text-sm">Email</th>
                  <th className="text-left px-6 py-4 text-[#A0AEC0] font-medium text-sm">Ebook</th>
                  <th className="text-center px-6 py-4 text-[#A0AEC0] font-medium text-sm">Marketing Consent</th>
                  <th className="text-left px-6 py-4 text-[#A0AEC0] font-medium text-sm">Downloaded At</th>
                  <th className="text-left px-6 py-4 text-[#A0AEC0] font-medium text-sm">Created At</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="5" className="px-6 py-12 text-center text-[#718096]">
                      <ArrowsClockwise className="w-6 h-6 animate-spin mx-auto mb-2" />
                      Loading downloads...
                    </td>
                  </tr>
                ) : downloads.length === 0 ? (
                  <tr>
                    <td colSpan="5" className="px-6 py-12 text-center text-[#718096]">
                      <Envelope className="w-8 h-8 mx-auto mb-2 opacity-50" />
                      No download records found
                    </td>
                  </tr>
                ) : (
                  downloads.map((download, index) => (
                    <tr 
                      key={download.id || index}
                      className="border-b border-[#2D3748] hover:bg-[#161B26]/50 transition-colors"
                    >
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2">
                          <Envelope className="w-4 h-4 text-[#6366F1]" />
                          <span className="text-white">{download.email}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span className="text-[#A0AEC0]">{download.ebook_title}</span>
                      </td>
                      <td className="px-6 py-4 text-center">
                        {download.marketing_consent ? (
                          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs bg-[#10B981]/20 text-[#10B981]">
                            <CheckCircle className="w-3 h-3" />
                            Yes
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs bg-[#EF4444]/20 text-[#EF4444]">
                            <XCircle className="w-3 h-3" />
                            No
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4 text-[#718096] text-sm">
                        {download.downloaded_at ? formatDate(download.downloaded_at) : 'Not yet'}
                      </td>
                      <td className="px-6 py-4 text-[#718096] text-sm">
                        {formatDate(download.created_at)}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          
          {/* Pagination */}
          {pagination.total > pagination.limit && (
            <div className="flex items-center justify-between px-6 py-4 border-t border-[#2D3748]">
              <button
                onClick={() => setPagination(prev => ({ ...prev, skip: Math.max(0, prev.skip - prev.limit) }))}
                disabled={pagination.skip === 0}
                className="px-4 py-2 bg-[#161B26] text-white rounded-lg hover:bg-[#2D3748] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              
              <span className="text-[#718096] text-sm">
                Page {Math.floor(pagination.skip / pagination.limit) + 1} of {Math.ceil(pagination.total / pagination.limit)}
              </span>
              
              <button
                onClick={() => setPagination(prev => ({ ...prev, skip: prev.skip + prev.limit }))}
                disabled={!pagination.hasMore}
                className="px-4 py-2 bg-[#161B26] text-white rounded-lg hover:bg-[#2D3748] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default EbookDownloadsManagement;
